// Gallery.jsx
import { Button, ButtonGroup } from "react-bootstrap";
import { header } from "./globalVariables";
import { api } from "./pureFunctions";
import { useDropboxImages } from "./useDropboxImages";
import { useState, useCallback, useEffect } from "react";

import arrowIcon from "./img/arrow.png";
import downloadIcon from "./img/downloadIcon.png";
import { SpinnerGroup } from "./Utils/SpinnerGroup";

const MAX_IMG = 5;
export default function Gallery({ folderName, setOpenMedia, setAIImages }) {
   const { loadedImages, hasMore, loadingList, loadingMore, loadMore, error } = useDropboxImages(folderName, "Bilder");

   const [fullImages, setFullImages] = useState({}); // path -> url
   const [currentImage, setCurrentImage] = useState();
   const [selectedImages, setSelectedImages] = useState([]);
   // Fetch (or reuse cached) link for a given index, and warm the browser cache.
   const resolveImage = useCallback(
      async (index) => {
         const img = loadedImages[index];
         if (!img) return null;

         if (fullImages[img.path]) {
            return { ...img, url: fullImages[img.path], index };
         }

         const res = await api.get(`/api/dropbox/image?path=${encodeURIComponent(img.path)}`);
         const url = res.data.url;

         setFullImages((prev) => ({ ...prev, [img.path]: url }));

         // Actually trigger the browser to download the bytes now.
         const preloadEl = new Image();
         preloadEl.src = url;

         return { ...img, url, index };
      },
      [loadedImages, fullImages],
   );

   async function getImageLink(index) {
      const resolved = await resolveImage(index);
      if (resolved) setCurrentImage(resolved);
   }

   async function openFullResImage(index) {
      const img = loadedImages[index];
      if (!img) return null;
      const res = await api.get(`/api/dropbox/full-res-link?path=${encodeURIComponent(img.path)}`);

      let url = res.data.url;
      url = url.replace(/([?&])dl=\d/, "$1raw=1");
      if (!url.includes("raw=1")) {
         url += (url.includes("?") ? "&" : "?") + "raw=1";
      }
      console.log(url);

      window.open(url, "_blank");
   }

   // Whenever the open image changes, silently prefetch its neighbors.
   useEffect(() => {
      if (currentImage == null) return;
      resolveImage(currentImage.index - 1);
      resolveImage(currentImage.index + 1);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [currentImage?.index]);

   if (loadingList)
      return (
         <div
            style={{
               position: "absolute",
               width: "100vw",
               display: "flex",
               justifyContent: "center",
               alignItems: "center",
               background: "#fffc",
               height: "calc(100% - 60px)",
               overflow: "hidden",
               pointerEvents: "none",
            }}
         >
            <SpinnerGroup></SpinnerGroup>
         </div>
      );
   if (error) return <p style={{ position: "absolute" }}>Fehler beim Laden der Bilder... {JSON.stringify(error)}</p>;

   return (
      <div
         style={{
            position: "absolute",
            top: 60,
            left: 0,
            zIndex: 10,
            width: "100%",
            height: "calc(100vh - 60px)",
            overflow: "auto",
            background: "linear-gradient(0deg, #222 0%, #222 100%)",
         }}
      >
         <div
            className="flex-row"
            style={{
               zIndex: 2,
               borderRadius: "12px",
               position: "absolute",
               right: 20,
               top: 20,
               background: "#fff7",
               fontFamily: "monospace",
            }}
         >
            <div className="mx-3">
               <strong style={{fontSize: 30,  color: selectedImages.length >= MAX_IMG ? "#d00" : "#000" }}>
                  {selectedImages.length}
               </strong>
               /{MAX_IMG}
            </div>
            <Button
               variant="dark"
               style={{
                  margin: 0,
                  padding: "4px 20px",
                  fontSize: 20,
                  fontWeight: 700,
                  aspectRatio: 1,
                  background: "#333a",
                  boxShadow: "0 0 10px #aaa",
               }}
               onClick={() => {
                  setOpenMedia(false);
               }}
            >
               x
            </Button>
         </div>

         <div>
            {currentImage && (
               <div key={currentImage.path} style={{ position: "relative", padding: 10 }}>
                  <div style={{ position: "absolute", left: 20, top: 20 }}>
                     <button
                        onClick={() => {
                           openFullResImage(currentImage.index);
                        }}
                        style={{
                           background: "#fff5",
                           backdropFilter: "blur(2px)",
                           border: "none",
                           aspectRatio: 1,
                           padding: 10,
                        }}
                     >
                        <img src={downloadIcon} width={40} />
                     </button>
                  </div>
                  {currentImage?.index > 0 && (
                     <div style={{ position: "absolute", left: 20, top: "50%" }}>
                        <button
                           onClick={() => getImageLink(currentImage.index - 1)}
                           style={{
                              background: "#fff5",
                              backdropFilter: "blur(2px)",
                              border: "none",
                              transform: "rotate(180deg)",
                              aspectRatio: 1,
                              padding: 20,
                           }}
                        >
                           <img src={arrowIcon} />
                        </button>
                     </div>
                  )}

                  <div style={{ position: "absolute", right: 20, top: "50%" }}>
                     {loadedImages.length - 1 > currentImage.index ? (
                        <button
                           onClick={() => getImageLink(currentImage.index + 1)}
                           style={{
                              background: "#fff5",
                              backdropFilter: "blur(2px)",
                              border: "none",
                              transform: "rotate(0deg)",
                              aspectRatio: 1,
                              padding: 20,
                           }}
                        >
                           <img src={arrowIcon} />
                        </button>
                     ) : (
                        <div style={{ width: "100px" }}>
                           <ReloadMore loadMore={loadMore} loadingMore={loadingMore} hasMore={hasMore} />
                        </div>
                     )}
                  </div>

                  <img src={currentImage.url || currentImage.thumbnail} alt="" style={{ width: "100%" }} />
               </div>
            )}
         </div>

         {currentImage ? (
            <div style={{ overflow: "auto", padding: "4px 4px", margin: "4px" }}>
               <div style={{ display: "flex", flexDirection: "row", gap: 0 }}>
                  {loadedImages.map((img, index) => (
                     <img
                        key={img.path}
                        onClick={() => getImageLink(index)}
                        onMouseEnter={() => resolveImage(index)} // bonus: prefetch on hover
                        src={img.thumbnail}
                        alt={img.name}
                        width={256}
                        style={{
                           ...(index === currentImage?.index ? { margin: 12, boxShadow: "0 0 12px 4px #0009" } : {}),
                        }}
                     />
                  ))}
                  <ReloadMore loadMore={loadMore} loadingMore={loadingMore} hasMore={hasMore} />
               </div>
            </div>
         ) : (
            <>
               <div style={{ margin: 8, display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
                  {loadedImages.map((img, index) => (
                     <div style={{ position: "relative" }}>
                        <img
                           key={img.path}
                           src={img.thumbnail || "placeholder.png"}
                           alt={img.name}
                           //  width={256}
                           style={{ width: "100%", cursor: "pointer", borderRadius: 4 }}
                           onClick={() => getImageLink(index)}
                           onMouseEnter={() => resolveImage(index)}
                        />
                        <Button
                           style={{ position: "absolute", left: 10, top: 10, height: 40, width: 40 }}
                           variant={selectedImages.includes(img.path) ? "dark" : "outline-dark"}
                           onClick={() => {
                              const prevImages = [...selectedImages];
                              if (prevImages.includes(img.path)) {
                                 setSelectedImages(prevImages.filter((item) => item !== img.path));
                              } else {
                                 if (prevImages.length < MAX_IMG) {
                                    setSelectedImages([...prevImages, img.path]);
                                 }
                              }
                           }}
                        >
                           {"   "}
                        </Button>
                     </div>
                  ))}
               </div>
               <div style={{ position: "fixed", bottom: 20, width: "100%", padding: 20 }}>
                  <ButtonGroup>
                     {setAIImages && <Button>Auswahl bestätigen</Button>}
                     {setOpenMedia && <Button>Auswahl bestätigen</Button>}
                  </ButtonGroup>
                  <ReloadMore loadMore={loadMore} loadingMore={loadingMore} hasMore={hasMore} />
               </div>
            </>
         )}
      </div>
   );
}

function ReloadMore({ loadMore, loadingMore, hasMore }) {
   return (
      <>
         {hasMore && (
            <div style={{ display: "flex", margin: 10, justifyContent: "center" }}>
               <button
                  style={{
                     background: "#ffa",
                     color: "#990",
                     fontWeight: 600,
                     border: "solid 2px #990",
                     padding: "10px 20px",
                     borderRadius: 8,
                  }}
                  variant="outline-dark"
                  onClick={loadMore}
                  disabled={loadingMore}
               >
                  {loadingMore ? <SpinnerGroup /> : "weitere Bilder laden"}
               </button>
            </div>
         )}
      </>
   );
}
